import { useState, useEffect, useCallback } from 'react';
import { SEC } from './SecurityTheme';
import { collectForensicData, getClients } from '../../services/api';
import { format, parseISO } from 'date-fns';
import { RefreshCw, Play, Clock, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

interface CollectionJob {
  clientId: string;
  clientName: string;
  status: 'pending' | 'running' | 'complete' | 'error';
  message?: string;
  startedAt?: string;
  completedAt?: string;
}

interface CollectionStatusProps {
  compact?: boolean;
}

export default function CollectionStatus({ compact = false }: CollectionStatusProps) {
  const [jobs, setJobs] = useState<CollectionJob[]>([]);
  const [collecting, setCollecting] = useState(false);
  const [lastRun, setLastRun] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Load last run from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('soc_last_collection');
    if (saved) setLastRun(saved);
    const savedJobs = localStorage.getItem('soc_collection_jobs');
    if (savedJobs) {
      try { setJobs(JSON.parse(savedJobs)); } catch { /* ignore */ }
    }
  }, []);

  const triggerCollection = useCallback(async () => {
    setCollecting(true);
    setError(null);
    const newJobs: CollectionJob[] = [];

    try {
      // Get all clients with tenant IDs
      const clientsData = await getClients({ page: 1, pageSize: 200 });
      const clients = clientsData.items.filter((c: any) => c.tenantId);

      if (clients.length === 0) {
        setError('No clients with Tenant IDs found');
        setCollecting(false);
        return;
      }

      // Initialize jobs
      for (const c of clients) {
        newJobs.push({
          clientId: c.id,
          clientName: c.companyName,
          status: 'pending',
        });
      }
      setJobs([...newJobs]);

      // Process sequentially to avoid overwhelming the API
      for (let i = 0; i < newJobs.length; i++) {
        newJobs[i].status = 'running';
        newJobs[i].startedAt = new Date().toISOString();
        setJobs([...newJobs]);

        try {
          const result = await collectForensicData({ clientId: newJobs[i].clientId });
          newJobs[i].status = result.success ? 'complete' : 'error';
          newJobs[i].message = result.message || (result.success ? `${result.userCount} users processed` : result.error);
          newJobs[i].completedAt = new Date().toISOString();
        } catch (err: any) {
          newJobs[i].status = 'error';
          newJobs[i].message = err.message || 'Collection failed';
          newJobs[i].completedAt = new Date().toISOString();
        }

        setJobs([...newJobs]);
      }

      const now = new Date().toISOString();
      setLastRun(now);
      localStorage.setItem('soc_last_collection', now);
      localStorage.setItem('soc_collection_jobs', JSON.stringify(newJobs));
    } catch (err: any) {
      setError(err.message || 'Failed to start collection');
    } finally {
      setCollecting(false);
    }
  }, []);

  const completed = jobs.filter(j => j.status === 'complete').length;
  const errors = jobs.filter(j => j.status === 'error').length;
  const total = jobs.length;
  const progress = total > 0 ? Math.round(((completed + errors) / total) * 100) : 0;

  const statusIcon = (status: string) => {
    switch (status) {
      case 'running': return <Loader2 className="h-3 w-3 animate-spin" style={{ color: SEC.cyan }} />;
      case 'complete': return <CheckCircle2 className="h-3 w-3" style={{ color: SEC.green }} />;
      case 'error': return <AlertCircle className="h-3 w-3" style={{ color: SEC.red }} />;
      default: return <Clock className="h-3 w-3" style={{ color: SEC.t3 }} />;
    }
  };

  if (compact) {
    return (
      <div
        className="flex items-center justify-between px-4 py-2 rounded-lg"
        style={{ background: SEC.panel2, border: `1px solid ${SEC.border}` }}
      >
        <div className="flex items-center gap-3">
          <RefreshCw className="h-4 w-4" style={{ color: SEC.cyanDim }} />
          <div>
            <span className="text-xs font-medium" style={{ color: SEC.t2 }}>Collection Engine</span>
            {lastRun && (
              <span className="text-[10px] ml-2" style={{ color: SEC.t3 }}>
                Last: {format(parseISO(lastRun), 'MMM d HH:mm')}
              </span>
            )}
          </div>
        </div>
        {collecting ? (
          <div className="flex items-center gap-2">
            <div className="w-24 h-1.5 rounded-full overflow-hidden" style={{ background: SEC.border }}>
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ width: `${progress}%`, background: SEC.cyan }}
              />
            </div>
            <span className="text-[10px] font-mono" style={{ color: SEC.cyan }}>{progress}%</span>
          </div>
        ) : (
          <button
            onClick={triggerCollection}
            className="flex items-center gap-1 text-[10px] font-medium px-2.5 py-1 rounded transition-colors"
            style={{
              color: SEC.cyan,
              border: `1px solid ${SEC.cyanDim}`,
              background: SEC.cyanGlow,
            }}
          >
            <Play className="h-3 w-3" />
            Collect
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      className="rounded-lg overflow-hidden"
      style={{ background: SEC.panel, border: `1px solid ${SEC.border}` }}
    >
      <div
        className="flex items-center justify-between px-4 py-3"
        style={{ borderBottom: `1px solid ${SEC.border}` }}
      >
        <div className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4" style={{ color: SEC.cyan }} />
          <h3 className="text-sm font-semibold tracking-wide uppercase" style={{ color: SEC.t1 }}>
            Collection Engine
          </h3>
        </div>
        <button
          onClick={triggerCollection}
          disabled={collecting}
          className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded transition-colors disabled:opacity-50"
          style={{
            color: collecting ? SEC.t3 : SEC.cyan,
            border: `1px solid ${collecting ? SEC.border : SEC.cyanDim}`,
            background: collecting ? 'transparent' : SEC.cyanGlow,
          }}
        >
          {collecting ? (
            <>
              <Loader2 className="h-3 w-3 animate-spin" />
              Collecting...
            </>
          ) : (
            <>
              <Play className="h-3 w-3" />
              Collect Now
            </>
          )}
        </button>
      </div>

      {/* Status bar */}
      <div className="px-4 py-2 flex items-center gap-4" style={{ borderBottom: `1px solid ${SEC.border}` }}>
        <div className="flex items-center gap-1.5">
          <Clock className="h-3 w-3" style={{ color: SEC.t3 }} />
          <span className="text-xs" style={{ color: SEC.t3 }}>
            Last run: {lastRun ? format(parseISO(lastRun), 'MMM d, yyyy HH:mm') : 'Never'}
          </span>
        </div>
        {collecting && total > 0 && (
          <>
            <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: SEC.border }}>
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ width: `${progress}%`, background: SEC.cyan }}
              />
            </div>
            <span className="text-xs font-mono" style={{ color: SEC.cyan }}>
              {completed + errors}/{total}
            </span>
          </>
        )}
      </div>

      {error && (
        <div className="px-4 py-2 text-xs" style={{ color: SEC.red, background: `${SEC.red}10` }}>
          {error}
        </div>
      )}

      {/* Jobs table */}
      {jobs.length > 0 && (
        <div className="overflow-y-auto" style={{ maxHeight: '300px' }}>
          <table className="w-full text-xs">
            <thead>
              <tr style={{ borderBottom: `1px solid ${SEC.border}` }}>
                <th className="text-left px-4 py-2 font-medium uppercase tracking-wider" style={{ color: SEC.t3, fontSize: '10px' }}>Client</th>
                <th className="text-left px-4 py-2 font-medium uppercase tracking-wider" style={{ color: SEC.t3, fontSize: '10px' }}>Status</th>
                <th className="text-left px-4 py-2 font-medium uppercase tracking-wider" style={{ color: SEC.t3, fontSize: '10px' }}>Details</th>
                <th className="text-right px-4 py-2 font-medium uppercase tracking-wider" style={{ color: SEC.t3, fontSize: '10px' }}>Time</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => (
                <tr
                  key={job.clientId}
                  className="transition-colors hover:bg-white/[0.02]"
                  style={{ borderBottom: `1px solid ${SEC.border}` }}
                >
                  <td className="px-4 py-2 font-medium" style={{ color: SEC.t1 }}>{job.clientName}</td>
                  <td className="px-4 py-2">
                    <span className="flex items-center gap-1.5">
                      {statusIcon(job.status)}
                      <span style={{ color: SEC.t2 }}>{job.status}</span>
                    </span>
                  </td>
                  <td className="px-4 py-2 truncate max-w-[200px]" style={{ color: SEC.t3 }}>
                    {job.message || '-'}
                  </td>
                  <td className="px-4 py-2 text-right font-mono" style={{ color: SEC.t4 }}>
                    {job.completedAt ? format(parseISO(job.completedAt), 'HH:mm:ss') : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
